import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Stack,
  ButtonBase,
  Avatar,
  IconButton
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { PurchaseOrder } from '@domain/purchasing';
import { format, isValid } from 'date-fns';
import { Paper } from '@shared/ui/common/paper';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { PurchaseOrderDraft } from '../../details/PurchaseOrderDraft';

interface PurchaseOrderDraftsTableProps {
  drafts: PurchaseOrder[];
}

export const PurchaseOrderDraftsTable: React.FC<PurchaseOrderDraftsTableProps> = ({
  drafts
}) => {
  const { openBottomSheet } = useBottomSheet();

  const handleEditDraft = (purchaseOrder: PurchaseOrder) => {
    if (!purchaseOrder.id) return;

    openBottomSheet({
      title: 'Edit Purchase Order Draft',
      component: <PurchaseOrderDraft purchaseOrderId={purchaseOrder.id} />,
      closeOnBackdropClick: false,
    });
  };

  const formatDate = (date: Date | undefined) => {
    if (!date || !isValid(new Date(date))) return '-';
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const getItemCount = (purchaseOrder: PurchaseOrder) => {
    return purchaseOrder.items?.length || 0;
  };



  // Don't render if no data
  if (!drafts || drafts.length === 0) {
    return (
      <Paper sx={{ p: 2, minHeight: '200px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No draft purchase orders found
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>PO Number</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Order Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Products</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drafts.map((draft) => (
              <TableRow
                key={draft.id}
                hover
                onClick={() => handleEditDraft(draft)}
                sx={{
                  cursor: 'pointer',
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {draft.poNumber || `No PO number`}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">
                    {formatDate(draft.orderDate)}
                  </Typography>
                </TableCell>


                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">

                    <Stack direction="row" spacing={1}>
                      {draft.items?.slice(0, 3).map((item, index) => (
                        <Stack direction="row" spacing={1} p={0.5} borderRadius={1} border={1} borderColor="divider" alignItems="center">


                          <Avatar
                            src={item.product.image}
                            variant="rounded"
                            sx={{ width: 30, height: 30, border: 1, borderColor: 'divider' }}
                          />

                          <Typography variant="caption" maxWidth="150px" sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}>
                            {item.product.title}
                          </Typography>


                        </Stack>

                      ))}

                      {draft.items?.length > 3 && (
                        <Stack direction="row" spacing={1} p={2} borderRadius={1} border={1} borderColor="divider" alignItems="center">
                          <Typography variant="caption">+{draft.items?.length - 2}</Typography>
                        </Stack>
                      )}

                    </Stack>

                  </Stack>
                </TableCell>





              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper >
  );
};
